import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import './CreateNewTrip.css'; // Import the CSS file
import { countryList } from './CountryList'; // make sure this is imported
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../layout/navbar';
import CityAutocomplete from './CityAutoComplete.js'; // Assuming you have a CityAutocomplete component
import CountryCityRows from './CountryCityRows'; // Assuming you have a CountryCityRows component
import { LoadScript } from "@react-google-maps/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const libraries = ["places"]; // <-- Move this OUTSIDE the component!

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const CreateNewTrip = () => {
    // using location (not the logic location but react useLocation hook to pass param between component , in this case the edited trip is store in param called state)
    const location = useLocation();
    const editMode = location.state && location.state.editMode;
    const tripToEdit = location.state && location.state.trip;

    const [tripName, setTripName] = useState(tripToEdit ? tripToEdit.tripName : '');
    const [description, setDescription] = useState(tripToEdit ? tripToEdit.description : '');
    const [startDate, setStartDate] = useState(tripToEdit ? new Date(tripToEdit.startDate) : null);
    const [endDate, setEndDate] = useState(tripToEdit ? new Date(tripToEdit.endDate) : null);
    const [selectedCountries, setSelectedCountries] = useState(tripToEdit ? (tripToEdit.countries || []).map(c => ({ label: c, value: c })) : []);
    const [fixedDates, setFixedDates] = useState(tripToEdit ? !!tripToEdit.fixedDates : true);
    const navigate = useNavigate();
    const [selectedCities, setSelectedCities] = useState(tripToEdit ? (tripToEdit.cities || []).map(c => ({ label: c, value: c })) : []);
    const autocompleteRef = useRef(null);
    const [countryCityRows, setCountryCityRows] = useState(() => {
      if (tripToEdit && Array.isArray(tripToEdit.countries) && Array.isArray(tripToEdit.cities)) {
        return tripToEdit.countries.map(countryName => {
          // Find the country object by label (name)
          const countryObj = countryList.find(c => c.label === countryName) || { label: countryName, value: "" };
          // Filter cities for this country using cityAndCountry
          const citiesForCountry = tripToEdit.cities.filter(city =>
            typeof city === 'object' &&
            city.cityAndCountry &&
            city.cityAndCountry.endsWith(`, ${countryName}`)
          );
          return {
            country: countryObj,
            cities: citiesForCountry.map((city, idx) => ({
              ...city,
              name: city.name,
              place_id: city.place_id || `${city.name}-${countryName}-${idx}`
            })),
            cityStr: citiesForCountry.map(city => city.name).filter(Boolean).join(', ')
          };
        });
      }
      return [{ country: null, cities: [] }];
    });

    const tripTypes = [
        { value: 'romantic', label: 'Romantic' },
        { value: 'extreme', label:  'Extreme' },
        { value: 'family', label:   'Family' },
        { value: 'solo', label:     'Solo' },
    ];

    const [type, setType] = useState(() => {
        if (tripToEdit && tripToEdit.type) {
            return tripTypes.find(opt => opt.value === tripToEdit.type) || null;
        }
        return null;
    });

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!tripName) {
            alert('Trip Name is mandatory');
            return;
        }
        if (fixedDates && (!startDate || !endDate)) {
            alert('Start Date and End Date are mandatory when Fixed Dates is enabled.');
            return;
        }

        const countries = countryCityRows.map(row => row.country?.label).filter(Boolean);
        const cities = countryCityRows.flatMap(row =>
          row.cities.map(city => ({
            name: city.name,
            cityAndCountry : city.cityAndCountry || `${city.name}, ${row.country.label}`,
            lat: city.lat,
            lng: city.lng
          }))
        );

        const tripData = {
            tripName,
            description,
            startDate: fixedDates ? startDate : null,
            endDate: fixedDates ? endDate : null,
            type: type ? type.value : null,
            countries,
            cities,
            fixedDates,
        };

        try {
            let response;
            if (editMode && tripToEdit && tripToEdit._id) {
                // PUT update, keep all days intact
                response = await fetch(`${BACKEND_URL}/newtrips/${tripToEdit._id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ ...tripToEdit, ...tripData, days: tripToEdit.days }),
                });
            } else {
                // POST create
                response = await fetch(`${BACKEND_URL}/newtrips`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(tripData),
                });
            }
            if (response.ok) {
                navigate('/userTrips');
                return;
            } else {
                let errorData = {};
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    try {
                        errorData = await response.json();
                    } catch (e) {}
                } else {
                    errorData.message = await response.text();
                }
                alert('Failed to save trip: ' + (errorData.message || response.statusText));
            }
        } catch (error) {
            alert('Error saving trip: ' + error.message);
        }
    };

    const isMobile = useIsMobile();

    return (
        <>
            <Navbar />
            <form
  onSubmit={handleSubmit}
  className={`trip-form${isMobile ? ' mobile' : ''}`}
  style={{
    position: 'relative',
    marginTop: isMobile ? 40 : 0 // Increase space below navbar for mobile
  }}
>
                <button
                    type="button"
                    className="close-button circle"
                    title="Cancel"
                    onClick={() => navigate('/userTrips')}
                    style={{ position: 'absolute', top: 18, right: 18, width: 36, height: 36, borderRadius: '50%', background: 'transparent', border: '1px solid #888', color: '#888', fontSize: '1.5em', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, cursor: 'pointer', transition: 'background 0.2s, color 0.2s' }}
                >
                    &times;
                </button>
                <h1>{editMode ? 'Edit Trip' : 'Create New Trip'}</h1>
                {isMobile ? (
      // Mobile: stacked
      <div className="form-row tripname-type-row">
        <label htmlFor="tripName" className="tripname-label">Trip Name:</label>
        <input
            type="text"
            id="tripName"
            value={tripName}
            onChange={(e) => setTripName(e.target.value)}
            required
            className="tripname-input"
        />
        <div className="type-group">
          <label htmlFor="type" className="type-label">Type:</label>
            <Select
                id="type"
                options={tripTypes}
                value={type}
                onChange={setType}
                placeholder="Select trip type"
                classNamePrefix="react-select"
                isSearchable={false}
                isClearable={false} // Add this to prevent empty row
            />
        </div>
      </div>
    ) : (
      // Desktop: all fields in one row
      <div className="form-row tripname-type-row" style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <label htmlFor="tripName" className="tripname-label" style={{ marginRight: 8 }}>Trip Name:</label>
        <input
          type="text"
          id="tripName"
          value={tripName}
          onChange={e => setTripName(e.target.value)}
          required
          className="tripname-input"
          style={{ flex: "1 1 220px", minWidth: 120, maxWidth: 260, marginRight: 16 }}
        />
        <label htmlFor="type" className="type-label" style={{ marginRight: 8 }}>Type:</label>
        <div className="type-select-container" style={{ minWidth: 160, maxWidth: 240, width: 220 }}>
          <Select
            id="type"
            options={tripTypes}
            value={type}
            onChange={setType}
            placeholder="Select trip type"
            classNamePrefix="react-select"
         
          />
        </div>
      </div>
    )}
                <div className="form-group description-group">
                    <label htmlFor="description">Description:</label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>
                <div style={{ textAlign: "left", alignItems:"left" , marginBottom: 0,fontSize :15 }} className="locations-label-row">
                  <label >Locations:</label>            
                <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={libraries}>
                  <CountryCityRows
                    countryCityRows={countryCityRows}
                    setCountryCityRows={setCountryCityRows}
                  />
                </LoadScript>
                </div>
                <div className="form-group fixed-dates-group">
                    {isMobile ? (
  <div className="date-range-frame" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <div className="fixed-dates-row" style={{ marginBottom: 8 }}>
      <label htmlFor="fixedDates" className="fixed-dates-label">Fixed Dates:</label>
      <input
        id="fixedDates"
        type="checkbox"
        checked={fixedDates}
        onChange={e => setFixedDates(e.target.checked)}
        style={{ marginLeft: 12 }}
      />
    </div>
    <div style={{ marginBottom: 8 }}>
      <label htmlFor="startDate" style={{ marginRight: 6 }}>Start Date:</label>
      <DatePicker
        id="startDate"
        selected={startDate}
        onChange={(date) => setStartDate(date)}
        dateFormat="MM/dd/yyyy"
        placeholderText="Select start date"
        disabled={!fixedDates}
      />
    </div>
    <div>
      <label htmlFor="endDate" style={{ marginRight: 6 }}>End Date:</label>
      <DatePicker
        id="endDate"
        selected={endDate}
        onChange={(date) => setEndDate(date)}
        dateFormat="MM/dd/yyyy"
        placeholderText="Select end date"
        disabled={!fixedDates}
      />
    </div>
  </div>
) : (
  <div className="date-range-frame">
  <div className="fixed-dates-row">
    <label htmlFor="fixedDates" className="fixed-dates-label">Fixed Dates:</label>
    <input
      id="fixedDates"
      type="checkbox"
      checked={fixedDates}
      onChange={e => setFixedDates(e.target.checked)}
      style={{ marginLeft: 12 }}
    />
  </div>
  <div className="dates-fields-row">
    <label htmlFor="startDate" style={{ marginRight: 6 }}>Start Date:</label>
    <DatePicker
        id="startDate"
        selected={startDate}
        onChange={(date) => setStartDate(date)}
        dateFormat="MM/dd/yyyy"
        placeholderText="Select start date"
        disabled={!fixedDates}
    />
    <label htmlFor="endDate" style={{ marginLeft: 24, marginRight: 6 }}>End Date:</label>
    <DatePicker
        id="endDate"
        selected={endDate}
        onChange={(date) => setEndDate(date)}
        dateFormat="MM/dd/yyyy"
        placeholderText="Select end date"
        disabled={!fixedDates}
    />
  </div>
</div>
)}
                </div>
                <div className="button-row">
                    <button type="submit" className="submit-button">
                        {editMode ? 'Save Changes' : 'Create Trip'}
                    </button>
                </div>
            </form>
        </>
    );
};

export default CreateNewTrip;